import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useState } from "react";
import './DragAndDropInput.scss';
import classNames from "classnames";
import { v4 } from "uuid";
import { Button, Col } from "react-bootstrap";

type DragAndDropInputProps = {
  onChange: (file: File[]) => void;
}

export default function DragAndDropInput({ onChange }: DragAndDropInputProps) {
  const [fileInfo, setFileInfo] = useState<{ id: string, file: File }[]>([]);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    onChange(fileInfo.map(info => info.file));
  }, [fileInfo, onChange]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const newFileInfo = Array.from(selectedFiles).map(file => ({ id: v4().substring(8), file }));
      setFileInfo(ff => ff.concat(newFileInfo));
    }
  };

  const handleRemoveFile = useCallback((index: number) => (evt: React.MouseEvent) => {
    evt.preventDefault();
    evt.stopPropagation();
    setFileInfo(prevFiles => prevFiles.filter((_, i) => i !== index));
  }, []);

  const handleClearAll = useCallback((evt: React.MouseEvent) => {
    evt.preventDefault();
    evt.stopPropagation();
    setFileInfo([]);
  }, []);

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.items;

    for (const item of droppedFiles) {
      const entry = item.webkitGetAsEntry();
      if (entry) {
        await traverseDirectory(entry);
      }
    }
    setIsActive(false);
  };

  const traverseDirectory = async (item: FileSystemEntry | FileSystemDirectoryEntry, path = "") => {
    if (item.isFile) {
      const file = await new Promise<File>((resolve, reject) => (item as FileSystemFileEntry).file(resolve, reject));
      setFileInfo(ff => ff.concat({ id: v4().substring(6), file: new File([file], `${path}/${file.name}`) }));
    } else if (item.isDirectory) {
      const dirReader = (item as FileSystemDirectoryEntry).createReader();
      const entries = await new Promise<FileSystemEntry[]>((resolve, reject) => dirReader.readEntries(resolve, reject));
      entries.forEach(async (entry) => {
        await traverseDirectory(entry, `${path}/${item.name}`);
      });
    }
  }

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsActive(false);
  }, []);

  return (
    <label htmlFor="browse" className="w-100">
      <input
        type="file"
        hidden
        id="browse"
        onChange={handleFileChange}
        multiple
      />
      <div className="drag-drop">
        <div
          className={classNames('document-uploader', { 'active': isActive })}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="upload-info mb-2">
            <FontAwesomeIcon size="lg" icon={solid('upload')} />
            <h6 className="mb-0 ms-3">Drag and drop your files here</h6>
          </div>
          <p className="mb-2">Browse files</p>

          {fileInfo.length > 0 && (
            <>
              <div className="d-flex align-items-center justify-content-between w-100 mb-2">
                <Col>
                  <small className="text-muted">{fileInfo.length} file{fileInfo.length > 0 && 's'} selected</small>
                </Col>
                <Button variant="danger" size="sm" onClick={handleClearAll}>
                  <FontAwesomeIcon icon={solid('trash')} />
                  <span className="ms-2">Clear all</span>
                </Button>
              </div>
              <div className="file-list">
                <div className="file-list-container">
                  {fileInfo.map((info, index) => (
                    <div className="file-item" key={info.id}>
                      <div className="file-info">{info.file.name}</div>
                      <div className="file-actions">
                        <FontAwesomeIcon onClick={handleRemoveFile(index)} icon={solid('times')} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </label>
  )
}